import type { MouseEvent, ReactElement } from 'react';
import clsx from 'clsx';
import React from 'react';
import { Menu } from '@headlessui/react';

import { LoadingText } from '@fragment/ui/src/components/LoadingText';

type BaseRow = {
  id: string;
};

type ChangeHandler<T> = (newValue: T, evt: MouseEvent) => void;

type RowRenderFunction<T> = (value: T) => ReactElement;

type RenderSelectorFunction = (children: ReactElement) => ReactElement;

type RenderChildrenFunction = (
  renderSelector: RenderSelectorFunction
) => ReactElement;

// The interface here is odd by necessity. Since the behavior of the drop-down depends on
// carefully-positioned relative and absolute divs (a detail we'd like to encapsulate here)
// this component needs to render both the dropdown and its parent container.
// We accept a render function as "children", whose first argument is, itself, a render function.
type PaginatedSelectorProps<T extends BaseRow> = {
  data: T[];
  selected?: T;
  onChange: ChangeHandler<T>;
  loadMore?: VoidFunction;
  fetching?: boolean;
  /** A function that accepts a row value and renders its drop-down option */
  renderRow: RowRenderFunction<T>;
  /** A function whose first argument is a render function for the visual dropdown selector. */
  children: RenderChildrenFunction;
};

export const PaginatedSelector = <T extends BaseRow>({
  data,
  selected,
  onChange,
  loadMore,
  fetching,
  renderRow,
  /** Render the selector and its parent container. */
  children: renderChildren,
}: PaginatedSelectorProps<T>): ReactElement => (
  <div className="relative">
    {renderChildren((children) => (
      <Menu>
        {() => (
          <>
            <Menu.Button
              className="max-w-full"
              data-testid="current-display-ws"
            >
              <span className="flex items-center justify-between flex-row w-full max-w-full min-w-f0 font-normal transition-colors text-main">
                {children}
              </span>
            </Menu.Button>
            <Menu.Items
              className="absolute border border-canvas bg-canvas overflow-y-auto max-h-f18 w-[calc(100%-8lh)]"
              data-testid="ws-dropdown"
            >
              {data.map((row) => {
                const rowSelected = row === selected;
                return (
                  <Menu.Item
                    key={row.id}
                    data-testid={row.id}
                    disabled={rowSelected}
                  >
                    {({ close }) => (
                      <button
                        type="button"
                        className={clsx(
                          'flex items-center justify-start flex-row w-full h-f2 p-f0 font-light truncate transition-colors',
                          rowSelected
                            ? 'cursor-default text-main-500'
                            : 'cursor-pointer text-main',
                          'bg-main-200 hover:bg-negative',
                          'border-b-[1px] border-canvas'
                        )}
                        onClick={(e: MouseEvent) => {
                          onChange(row, e);
                          close();
                        }}
                      >
                        {renderRow(row)}
                      </button>
                    )}
                  </Menu.Item>
                );
              })}
              {loadMore && (
                <Menu.Item>
                  <button
                    type="button"
                    className={clsx(
                      'block w-full h-f4 py-f1 px-f2 text-sm font-light truncate',
                      'text-main-500 bg-canvas hover:bg-negative transition-colors'
                    )}
                    data-testid="load-more"
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      loadMore();
                      e.preventDefault();
                    }}
                    disabled={fetching}
                  >
                    {!fetching ? 'Load More' : <LoadingText text="Loading" />}
                  </button>
                </Menu.Item>
              )}
            </Menu.Items>
          </>
        )}
      </Menu>
    ))}
  </div>
);
